import { Component, model, input, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { LineItemModel } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { TaskManagementService } from '../../../../services/task-management.service';
import { ProjectTask } from '../../../../models/task-management.models';
import { ValidationService } from '../../../../services/validation.service';

@Component({
  selector: 'app-tasks-control',
  standalone: true,
  imports: [CommonModule, FormsModule, Select, ButtonModule],
  template: `
    <div class="checklist-control-row">
      <label [for]="'tasks-' + lineItem().lineItemId" class="checklist-control-label">
        {{ lineItem().name }}:@if (lineItem().isRequired) { <span class="required-indicator">*</span>}
        <button
          type="button"
          pButton
          icon="pi pi-volume-up"
          [rounded]="true"
          [text]="true"
          size="small"
          (click)="speakLabel()"
          class="speaker-btn ml-1"
          [attr.aria-label]="'Read label: ' + lineItem().name"
        ></button>
      </label>
      <div class="checklist-control-input">
        <p-select
          [inputId]="'tasks-' + lineItem().lineItemId"
          [options]="taskOptions()"
          [(ngModel)]="value"
          (onChange)="onSelectionChange()"
          [disabled]="disabled() || loading()"
          [invalid]="hasError()"
          [placeholder]="loading() ? 'Loading tasks...' : 'Select a task...'"
          optionLabel="displayText"
          optionValue="taskId"
          [filter]="true"
          filterBy="displayText"
          styleClass="w-full"
          appendTo="body"
        />
        @if (hasError()) {
          @for (err of errors(); track $index) {
            <small class="p-error d-block">{{ err }}</small>
          }
        }
      </div>
    </div>
  `,
  styles: [`
    .checklist-control-row {
      display: grid;
      grid-template-columns: minmax(150px, 200px) 1fr;
      gap: 1rem;
      align-items: center;
      padding: 0.5rem;
      border-bottom: 1px solid var(--surface-border);
    }
    .checklist-control-label {
      font-weight: 500;
      text-align: left;
      padding-right: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }
    .required-indicator {
      color: var(--color-error-mid);
      font-weight: bold;
      margin-left: 2px;
    }
    .speaker-btn {
      flex-shrink: 0;
    }
  `]
})
export class TasksControlComponent implements OnInit {
  lineItem = input.required<LineItemModel>();
  value = model<number | null>(null);
  disabled = input<boolean>(false);
  projectId = input.required<number>();
  showError = input<boolean>(false);

  loading = signal<boolean>(false);
  taskOptions = signal<{ taskId: number; displayText: string }[]>([]);
  touched = signal(false);

  private speechService = inject(SpeechService);
  private taskManagementService = inject(TaskManagementService);
  private validationService = inject(ValidationService);

  errors = computed(() => {
    if (!this.showError() && !this.touched()) return [];
    return this.validationService.validate(this.lineItem(), this.value());
  });

  hasError = computed(() => this.errors().length > 0);

  ngOnInit(): void {
    this.loadTasks();
  }

  onSelectionChange(): void {
    this.touched.set(true);
  }

  private loadTasks(): void {
    const projectId = this.projectId();
    if (!projectId || projectId === 0) {
      console.warn('ProjectId is not available for tasks control');
      return;
    }

    this.loading.set(true);
    this.taskManagementService.getTasksByProject(projectId).subscribe({
      next: (tasks: ProjectTask[]) => {
        // Transform tasks to dropdown options with format "title #taskId"
        const options = tasks.map(task => ({
          taskId: task.taskId,
          displayText: `${task.title} #${task.taskId}`
        }));
        this.taskOptions.set(options);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading tasks:', error);
        this.loading.set(false);
        this.taskOptions.set([]);
      }
    });
  }

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }
}
